<template>
  <div class="modal-window">
    <button type="button" @click="$emit('close')" class="button button--close">
      <IconClose class="icon icon--medium-large" />
    </button>
    <form>
      <div class="layout--text-left modal-window__wrap">
        <Legend class="modal-window__title">{{ modalTitle }}</Legend>
        <div class="input-group__field-wrap input-group__field-wrap--wide">
          <label for="cardNumber" class="input-group__label">
            Card Number
          </label>
          <div class="modal-window__credit-card-input-wrap">
            <input
              type="tel"
              class="input-group__input input-group__input--wide layout--margin-bottom-xs"
              id="cardNumber"
              name="cardNumber"
              v-model="cardNumber"
              v-mask="cardMask"
            />
            <div class="modal-window__credit-card-logo-input">
              <font-awesome-icon
                v-if="creditCardIcon"
                :icon="['fab', creditCardIcon]"
                class="text-size-md"
              />
            </div>
            <div
              id="cardNumberError"
              class="input-group__error"
              v-if="!$v.cardNumber.required && $v.cardNumber.$dirty"
              role="alert"
            >
              Please enter a card number
            </div>
          </div>
        </div>
        <fieldset>
          <legend class="input-group__label">Expiration Date</legend>
          <div class="input-group__wrap">
            <div class="input-group__field-wrap">
              <label
                for="expirationMonth"
                class="input-group__label layout--screen-reader-only"
              >
                exp month
              </label>
              <div class="input-group__select-wrap layout--full-width">
                <select
                  class="input-group__input input-group__select input-group__input--wide layout--margin-bottom-xs"
                  id="expirationMonth"
                  name="expirationMonth"
                  v-model="expirationMonth"
                >
                  <option value="" disabled selected hidden>
                    Month
                  </option>
                  <option
                    v-for="month in months"
                    :key="month.monthValue"
                    :value="month.monthValue"
                    >{{ month.monthName }}</option
                  >
                </select>
              </div>
              <div
                id="expirationMonthError"
                class="input-group__error"
                v-if="!$v.expirationMonth.required && $v.expirationMonth.$dirty"
                role="alert"
              >
                Required
              </div>
            </div>
            <div class="input-group__field-wrap">
              <label
                for="expirationYear"
                class="input-group__label layout--screen-reader-only"
              >
                exp year
              </label>
              <div class="input-group__select-wrap layout--full-width">
                <select
                  class="input-group__input input-group__select input-group__input--wide layout--margin-bottom-xs"
                  id="expirationYear"
                  name="expirationYear"
                  v-model="expirationYear"
                >
                  <option value="" disabled selected hidden>
                    Year
                  </option>
                  <option v-for="year in years" :key="year" :value="year">{{
                    year
                  }}</option>
                </select>
              </div>
              <div
                id="expirationYearError"
                class="input-group__error"
                v-if="!$v.expirationYear.required && $v.expirationYear.$dirty"
                role="alert"
              >
                Required
              </div>
            </div>
          </div>
        </fieldset>
        <div class="input-group__wrap">
          <div class="input-group__field-wrap layout--margin-bottom-sm">
            <label for="zip" class="input-group__label">
              Zip Code
            </label>
            <input
              type="text"
              class="input-group__input input-group__input--wide"
              id="zip"
              name="zip"
              v-model="zip"
            />
            <div
              id="zipError"
              class="input-group__error"
              v-if="!$v.zip.required && $v.zip.$dirty"
              role="alert"
            >
              Required
            </div>
          </div>
          <div class="input-group__field-wrap layout--margin-bottom-sm">
            <label for="securityCode" class="input-group__label">
              Security Code
            </label>
            <input
              type="text"
              class="input-group__input input-group__input--wide"
              id="securityCode"
              name="securityCode"
              v-model="securityCode"
              v-mask="cVVMask"
            />
            <div
              class="input-group__icon"
              v-tooltip="{
                content:
                  'The CVV number is a 3 digit number located on the back of Discover, Mastercard, and Visa Cards. It is a 4 digit number on the front of American Express cards.',
                classes: ['modal-window__credit-card-tooltip'],
                placement: 'right-end',
              }"
            >
              <IconQuestion class="icon icon--medium-large" />
            </div>
            <div
              id="securityCodeError"
              class="input-group__error"
              v-if="!$v.securityCode.required && $v.securityCode.$dirty"
              role="alert"
            >
              Required
            </div>
          </div>
        </div>
        <v-checkbox v-model="agreement">
          <template v-slot:label>
            <div>
              I agree to the
              <a href="http://motionmailapp.com/terms" class="link" @click.stop
                >Terms of Use</a
              >
              and
              <a
                href="http://motionmailapp.com/privacy"
                class="link"
                @click.stop
                >Privacy Policy</a
              >
            </div>
          </template>
        </v-checkbox>
        <div 
          v-if="suspendedMessage" 
          class="transaction-summary__message layout--margin-bottom-md"
        >
          <IconAlert class="dashboard__icon" />
          <p>By entering a new credit card, you will bring your account up to date by being billed your last failed charge.</p>
        </div>
        <Error
          :invalid="!$v.agreement.sameAs && $v.agreement.$dirty"
          message="You must agree in order to continue"
        />
        <div class="layout--flex-space-between layout--margin-top-sm">
          <LoadingButton
            :event="handleUpdate"
            :loading="$wait.is('updating credit card')"
            :doneLoading="saved"
          />
          <button
            type="button"
            class="button button--secondary"
            @click="$emit('close')"
          >
            Cancel
          </button>
        </div>
        <Error
          :invalid="updateError"
          :message="updateErrorMessage || updateErrorDefaultMessage"
        />
      </div>
    </form>
  </div>
</template>

<script>
import {
  getMonths,
  getYears,
  creditCardIconFromNumber,
  creditCardBrandFromNumber,
} from "../helpers/helpers";

import { required, sameAs } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import { mapWaitingActions } from "vue-wait";

import Error from "./Error";
import LoadingButton from "./LoadingButton";

import IconClose from "../assets/icons/iconCloseX.svg";
import IconQuestion from "../assets/icons/iconQuestionMark.svg";
import IconAlert from "../assets/icons/iconAlertBar-Exclamation.svg";

export default {
  name: "updateCardModal",
  components: {
    Error,
    LoadingButton,
    IconClose,
    IconQuestion,
    IconAlert,
  },
  props: {
    suspendedMessage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cardNumber: "",
      expirationMonth: "",
      expirationYear: "",
      zip: "",
      securityCode: "",
      updateError: false,
      updateErrorDefaultMessage: "Error saving card. Please ensure card information is entered correctly and card is not expired.",
      updateErrorMessage: "",
      agreement: false,
      saved: false,
    };
  },
  computed: {
    ...mapGetters("account", ["subscription", "defaultCard"]),
    months: function() {
      return getMonths();
    },
    years: function() {
      return getYears();
    },
    // change masking pattern if card is Amex
    cardMask: function() {
      if (this.cardNumber.length > 0) {
        const firstDigits = this.cardNumber.substring(0, 2);
        if (firstDigits === "34" || firstDigits === "37") {
          return ["####-######-#####"];
        }
      }
      return ["####-####-####-####"];
    },
    creditCardIcon: function() {
      return creditCardIconFromNumber(this.cardNumber);
    },
    creditCardBrand: function() {
      return creditCardBrandFromNumber(this.cardNumber);
    },
    cVVMask: function() {
      if (this.creditCardBrand) {
        if (this.creditCardBrand === "American Express") {
          return ["####"];
        }
      }
      return ["###"];
    },
    modalTitle: function() {
      if (this.defaultCard) {
        return "Update Credit Card";
      }
      return "Add Credit Card";
    },
  },
  methods: {
    ...mapWaitingActions("account", {
      updateCreditCard: "updating credit card",
    }),
    handleUpdate: function() {
      this.updateError = false;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.saved = false;
        this.updateErrorMessage = "";
        const payload = {
          subscriptionType: this.subscription.subscriptionType,
          billingCycle: this.subscription.billingCycle,
          cardNumber: this.cardNumber,
          expMonth: this.expirationMonth,
          expYear: this.expirationYear.toString(),
          postalCode: this.zip,
          cvv: this.securityCode,
        };
        this.updateCreditCard(payload)
          .then(() => {
            this.saved = true;
            this.$emit("close");
          })
          .catch(error => {
            this.updateError = true;
            this.updateErrorMessage = error.response.data.message;
          });
      }
   },
    handleChange: function() {
      this.saved = false;
      this.updateError = false;
    },
  },
  validations: {
    securityCode: { required },
    cardNumber: { required },
    expirationMonth: { required },
    expirationYear: { required },
    zip: { required },
    agreement: {
      sameAs: sameAs(() => true),
    },
  },
};
</script>

<style lang="scss">
@import "../styles/components/_modal.scss";
</style>
